"use strict";
// @dunea/shared plugins — the plugin SDK, the first-party catalog and the
// helpers that derive today's hand-kept lists (INTEGRATIONS, detect tokens,
// skill index) from it.
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __exportStar = (this && this.__exportStar) || function(m, exports) {
    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PLATFORM_CODE_DIRS = exports.PLUGINS = void 0;
exports.definePlugin = definePlugin;
exports.toIntegrationManifest = toIntegrationManifest;
exports.integrationsFromPlugins = integrationsFromPlugins;
exports.pluginDetectionTokenMap = pluginDetectionTokenMap;
exports.pluginInstallClosure = pluginInstallClosure;
exports.pluginDependents = pluginDependents;
exports.lockedPathsFor = lockedPathsFor;
exports.platformFileOwner = platformFileOwner;
exports.projectRelativePath = projectRelativePath;
exports.agentPluginCatalog = agentPluginCatalog;
const catalog_1 = require("./catalog");
const registry_1 = require("./registry");
__exportStar(require("./types"), exports);
__exportStar(require("./registry"), exports);
__exportStar(require("./explain"), exports);
__exportStar(require("./core-points"), exports);
__exportStar(require("./catalog"), exports);
__exportStar(require("./contracts/payments"), exports);
__exportStar(require("./contracts/http"), exports);
__exportStar(require("./contracts/marketing"), exports);
__exportStar(require("./runtime/marketing-runtime"), exports);
/** Identity helper that type-checks a manifest. */
function definePlugin(manifest) {
    return manifest;
}
/** The registry of every first-party plugin. Throws at load if the catalog breaks the contract. */
exports.PLUGINS = (0, registry_1.createPluginRegistry)(catalog_1.FIRST_PARTY_PLUGINS);
/** A plugin with a tile, as the legacy IntegrationManifest the Integrations panel and studio use. */
function toIntegrationManifest(plugin) {
    if (!plugin.tile) {
        return undefined;
    }
    const { category, icon, invokePrompt, skill, credentials, broker } = plugin.tile;
    const detect = plugin.targets?.['web-app']?.detect;
    return {
        id: plugin.id,
        category,
        title: plugin.title,
        description: plugin.description,
        icon,
        invokePrompt,
        skill,
        credentials: {
            mode: credentials.mode,
            ...(credentials.redirectUri ? { redirectUri: credentials.redirectUri } : {}),
            fields: credentials.fields.map((f) => ({ ...f })),
        },
        ...(broker ? { broker } : {}),
        ...(detect ? { detect: [...detect] } : {}),
    };
}
/** Every tile-bearing plugin, in catalog order. */
function integrationsFromPlugins(manifests = catalog_1.FIRST_PARTY_PLUGINS) {
    return manifests.map(toIntegrationManifest).filter((m) => Boolean(m));
}
/** All web-app source markers → plugin id (superset of the legacy integration detect map). */
function pluginDetectionTokenMap(manifests = catalog_1.FIRST_PARTY_PLUGINS) {
    const map = {};
    for (const m of manifests) {
        for (const token of m.targets?.['web-app']?.detect ?? []) {
            map[token] = m.id;
        }
    }
    return map;
}
/** The plugin plus every capability it (transitively) requires, dependencies first. */
function pluginInstallClosure(pluginId, registry = exports.PLUGINS) {
    const order = [];
    const seen = new Set();
    const visit = (id) => {
        if (seen.has(id)) {
            return;
        }
        seen.add(id);
        for (const [point, req] of Object.entries(registry.get(id)?.requires ?? {})) {
            if (req.optional) {
                continue;
            }
            // Required capabilities with an app half (payments for shop). Provider
            // plugins (stripe, razorpay) are host-only and live in their own settings.
            for (const { plugin } of registry.providersOf(point)) {
                if (plugin.kind === 'capability' && plugin.id !== id) {
                    visit(plugin.id);
                }
            }
        }
        order.push(id);
    };
    visit(pluginId);
    return order;
}
/** Enabled plugins that can't run without `pluginId` (blocks disabling it). */
function pluginDependents(pluginId, enabled, registry = exports.PLUGINS) {
    return enabled.filter((id) => id !== pluginId && pluginInstallClosure(id, registry).includes(pluginId));
}
/** Paths the agent must not edit for a set of active plugins on a surface. */
function lockedPathsFor(active, surface = 'web-app', registry = exports.PLUGINS) {
    const out = new Set();
    for (const id of active) {
        for (const p of registry.get(id)?.targets?.[surface]?.lockedPaths ?? []) {
            out.add(p);
        }
    }
    return [...out].sort();
}
/** Directories that hold platform (plugin app-half) code in every generated app. */
exports.PLATFORM_CODE_DIRS = ['frontend/worker/dunea/', 'frontend/src/dunea/'];
/**
 * If `filePath` is platform-managed plugin code the agent must not edit,
 * returns the plugin that owns it ('platform' for shared dirs); otherwise null.
 * Accepts project-relative or absolute pod paths (/home/project/...).
 */
function platformFileOwner(filePath, registry = exports.PLUGINS) {
    const rel = projectRelativePath(filePath);
    for (const m of registry.all()) {
        for (const target of Object.values(m.targets ?? {})) {
            if (target.lockedPaths?.includes(rel)) {
                return m.id;
            }
        }
    }
    // Also match a platform dir below some prefix (`project/frontend/worker/dunea/…`),
    // as the original substring guard did.
    return exports.PLATFORM_CODE_DIRS.some((dir) => rel.startsWith(dir) || rel.includes(`/${dir}`)) ? 'platform' : null;
}
/**
 * Canonical project-relative form of an agent-supplied path: backslashes to
 * slashes, `.`/`..`/empty segments resolved (POSIX semantics, never above the
 * root), and the pod's `/home/project/` prefix removed. Guards must compare
 * this form: `frontend//worker/dunea/x`, `frontend/./worker/dunea/x` and
 * `frontend/../frontend/worker/dunea/x` all name `frontend/worker/dunea/x`.
 * (No node:path: @dunea/shared also runs in the browser.)
 */
function projectRelativePath(filePath) {
    const out = [];
    for (const seg of filePath.replace(/\\/g, '/').split('/')) {
        if (seg === '' || seg === '.') {
            continue;
        }
        if (seg === '..') {
            out.pop();
        }
        else {
            out.push(seg);
        }
    }
    if (out[0] === 'home' && out[1] === 'project') {
        out.splice(0, 2);
    }
    return out.join('/');
}
/**
 * The agent's plugin catalog: one line per capability on this surface, marked
 * enabled or not. Full skills are only loaded for enabled plugins; the agent
 * proposes enabling the rest (it never enables silently).
 */
function agentPluginCatalog(enabled, surface = 'web-app', registry = exports.PLUGINS) {
    const on = new Set(enabled);
    const lines = registry
        .all()
        .filter((m) => m.kind === 'capability' && m.agentCard && (m.surfaces ?? []).includes(surface))
        .map((m) => `- [${on.has(m.id) ? 'enabled' : 'available'}] ${m.agentCard}`);
    return lines.join('\n');
}
